/**
 * azure-functions/shared/roleCompetence.cjs
 *
 * The competence a role requires (ISO/IEC 27001:2022 clause 7.2), and whether
 * one employee meets it as of a date. Canonical copy — src/utils/roleCompetence.js
 * re-exports it, so the browser and the server answer from one implementation
 * (the same arrangement as shared/trainingDerivations.cjs).
 *
 * ── Shapes (docs/state-schema.md § roleDefinitions, § Employee) ─────────────
 *
 *   roleDefinitions[].competenceRequirements[] = {
 *     id, kind: 'education'|'training'|'experience'|'skill'|'certification',
 *     description, criterionId?, requiredLevel?, trainingModuleId?,
 *     trainingProgramId?, trainingCompletionSuffices? (training kind only),
 *     reassessEveryMonths?, evidenceRequired }
 *   roleDefinitions[].competenceHistory[] = { at, by, name, requirements[] }
 *     append-only, stamped by state-write (stampCompetenceHistory below),
 *     never by the client.
 *   employees[].competenceAssessments[] = {
 *     id, roleId, requirementId, level, met, effectivenessAssessed,
 *     evidence: { artefactId?, note? } | null, expiresAt, assessedAt,
 *     assessedBy, assessedByName }
 *     append-only, written only by employees-write?action=record-competence-assessment.
 *
 * ── The rules (rcq-role-competence-session-plan.md, "Agreed design") ────────
 *   - The required level is read from the ROLE, as of the date asked about,
 *     never from a target copied onto the employee (TRYGGR_INTENT §10).
 *   - Assignment and acknowledgement never make anything met (IDN-B-001):
 *     this derivation does not look at assignedRoles[] at all.
 *   - Not assessed and expired are unmet — never excluded (§12).
 *   - A training completion never meets a `skill` requirement; a `training`
 *     requirement is met by a current completion only when it says
 *     `trainingCompletionSuffices: true` (7.2(c), effectiveness).
 *   - A `skill` assessment counts only when it says effectiveness was assessed.
 */

'use strict';

const {
  currentTrainingEvidence, effectiveValidUntil, trainingModulesOf, trainingProgramsOf,
} = require('./trainingDerivations.cjs');
const { isSampleEmployee } = require('./sampleRecords.cjs');

const COMPETENCE_KINDS = Object.freeze(['education', 'training', 'experience', 'skill', 'certification']);
const COMPETENCE_STATUSES = Object.freeze(['met', 'gap', 'not-assessed', 'expired']);

function toMs(value) {
  if (value == null || value === '') return NaN;
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : NaN;
}

function asOfMs(asOf) {
  if (asOf == null) return Date.now();
  const ms = toMs(asOf instanceof Date ? asOf.getTime() : asOf);
  return Number.isFinite(ms) ? ms : Date.now();
}

/** Calendar months added in UTC; a day that does not exist clamps to month end. */
function addMonthsMs(ms, months) {
  const d = new Date(ms);
  const day = d.getUTCDate();
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1,
    d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds()));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.getTime();
}

function requirementsOf(role) {
  return Array.isArray(role?.competenceRequirements) ? role.competenceRequirements.filter(Boolean) : [];
}

function historyOf(role) {
  return Array.isArray(role?.competenceHistory)
    ? role.competenceHistory.filter((h) => h && Number.isFinite(toMs(h.at)))
    : [];
}

/**
 * The requirements a role stated as of `asOf`. With a history, the latest
 * entry at or before that instant; before the first entry the role required
 * nothing (the requirements did not exist yet). Without one — a role nobody
 * has saved since the history began — the current list.
 */
function competenceRequirementsAsOf(role, asOf) {
  const history = historyOf(role);
  if (history.length === 0) return requirementsOf(role);
  const at = asOfMs(asOf);
  let found = null;
  for (const h of history) {
    const ms = toMs(h.at);
    if (ms <= at && (!found || ms >= toMs(found.at))) found = h;
  }
  return found && Array.isArray(found.requirements) ? found.requirements.filter(Boolean) : [];
}

/**
 * Every name the role has been known by, current first — legacy roleScores
 * are keyed by role name, so a rename recorded in the history keeps them
 * reachable.
 */
function roleNamesOf(role) {
  const names = [];
  const add = (n) => {
    const t = typeof n === 'string' ? n.trim() : '';
    if (t && !names.includes(t)) names.push(t);
  };
  add(role?.name);
  const history = historyOf(role).slice().sort((a, b) => toMs(b.at) - toMs(a.at));
  for (const h of history) add(h.name);
  return names;
}

/**
 * The legacy per-role score for a criterion: `evaluations[criterionId].roleScores`
 * keyed by role id (new) or by role name (every record written before
 * RCQA-01 — EmployeeDetailModal keys it by name). Id wins over name. Read
 * only; nothing here rewrites a stored record.
 */
function legacyRoleScore(employee, role, criterionId) {
  const evals = employee?.evaluations;
  if (!evals || typeof evals !== 'object' || Array.isArray(evals) || !criterionId) return null;
  const entry = evals[criterionId];
  const scores = entry && typeof entry.roleScores === 'object' && entry.roleScores ? entry.roleScores : null;
  if (!scores) return null;
  const keys = [role?.id, ...roleNamesOf(role)].filter(Boolean);
  for (const k of keys) {
    const rs = scores[k];
    if (rs && typeof rs === 'object') return { key: k, ...rs };
  }
  return null;
}

function assessmentsFor(employee, roleId, requirementId, at) {
  const list = Array.isArray(employee?.competenceAssessments) ? employee.competenceAssessments : [];
  return list.filter(
    (a) => a && a.roleId === roleId && a.requirementId === requirementId && !(toMs(a.assessedAt) > at)
  );
}

function latest(list) {
  let best = null;
  for (const a of list) {
    const ms = toMs(a.assessedAt);
    const bestMs = best ? toMs(best.assessedAt) : NaN;
    if (!best || (Number.isFinite(ms) && (!Number.isFinite(bestMs) || ms >= bestMs))) best = a;
  }
  return best;
}

function hasEvidence(evidence) {
  if (!evidence || typeof evidence !== 'object') return false;
  const id = typeof evidence.artefactId === 'string' ? evidence.artefactId.trim() : '';
  const note = typeof evidence.note === 'string' ? evidence.note.trim() : '';
  return Boolean(id || note);
}

function isLevelBased(req) {
  return Boolean(req.criterionId) && Number.isFinite(Number(req.requiredLevel)) && req.requiredLevel !== null;
}

/** Current training evidence for a training-kind requirement, or null. */
function trainingEvidenceFor(req, employee, ctx, at) {
  if (req.kind !== 'training' || req.trainingCompletionSuffices !== true) return null;
  const modules = Array.isArray(ctx.trainingModules) ? ctx.trainingModules : [];
  const records = ctx.evidenceRecords;
  let moduleIds = [];
  if (req.trainingProgramId) {
    const programs = Array.isArray(ctx.trainingPrograms) ? ctx.trainingPrograms : [];
    const prog = programs.find((p) => p && p.id === req.trainingProgramId);
    moduleIds = prog ? (prog.modules ?? []).map((m) => m && m.moduleId).filter(Boolean) : [];
  } else if (req.trainingModuleId) {
    moduleIds = [req.trainingModuleId];
  }
  if (moduleIds.length === 0) return null;
  let earliestExpiry = null;
  let latestPass = null;
  for (const id of moduleIds) {
    const course = modules.find((m) => m && m.id === id);
    const rec = course ? currentTrainingEvidence(course, employee, records, at) : null;
    if (!rec) return null;
    const done = rec.reviewedAt || rec.submittedAt || rec.createdAt || null;
    // A pass recorded after the date asked about did not exist then.
    if (!Number.isFinite(toMs(done)) || toMs(done) > at) return null;
    const exp = effectiveValidUntil(course, rec);
    if (exp && (!earliestExpiry || toMs(exp) < toMs(earliestExpiry))) earliestExpiry = exp;
    if (done && (!latestPass || toMs(done) > toMs(latestPass))) latestPass = done;
  }
  return { assessedAt: latestPass, expiresAt: earliestExpiry };
}

/**
 * One requirement's status for one employee as of `at` (ms).
 * @returns {{ requirement, status, reason, requiredLevel, assessment, source, expiresAt }}
 */
function statusOfRequirement(req, role, employee, ctx, at) {
  const requiredLevel = isLevelBased(req) ? Number(req.requiredLevel) : null;
  const base = { requirement: req, requiredLevel };

  const training = trainingEvidenceFor(req, employee, ctx, at);
  if (training) {
    // The requirement's own interval applies on top of the module's validity.
    let expiresAt = training.expiresAt;
    const m = Number(req.reassessEveryMonths);
    if (Number.isFinite(m) && m > 0 && Number.isFinite(toMs(training.assessedAt))) {
      const byInterval = addMonthsMs(toMs(training.assessedAt), m);
      if (!expiresAt || byInterval < toMs(expiresAt)) expiresAt = new Date(byInterval).toISOString();
    }
    if (expiresAt && toMs(expiresAt) <= at) {
      return { ...base, status: 'expired', reason: 'reassessment-due', assessment: null, source: 'training', expiresAt, trainingCompletedAt: training.assessedAt };
    }
    return { ...base, status: 'met', reason: 'training-current', assessment: null, source: 'training', expiresAt, trainingCompletedAt: training.assessedAt };
  }

  // A level means nothing without the scale it was set on. A requirement
  // naming a criterion the tenant no longer has (or a caller that did not
  // pass the catalogue) cannot be judged met, whatever was recorded (§6.2).
  if (req.criterionId) {
    const criteria = Array.isArray(ctx.criteria) ? ctx.criteria : [];
    if (!criteria.some((c) => c && c.id === req.criterionId)) {
      return { ...base, status: 'gap', reason: 'criterion-removed', assessment: null, source: null, expiresAt: null };
    }
  }

  // A server-stamped assessment always outranks a legacy score: the legacy
  // field is client-written, so it may never override one (co-owner, RCQA-01).
  const candidates = [];
  const recorded = latest(assessmentsFor(employee, role.id, req.id, at));
  if (recorded) candidates.push({ source: 'assessment', a: recorded });
  if (!recorded && req.criterionId) {
    const legacy = legacyRoleScore(employee, role, req.criterionId);
    if (legacy && legacy.score != null && !(toMs(legacy.updatedAt) > at)) {
      candidates.push({
        source: 'legacy-role-score',
        a: {
          level: Number(legacy.score),
          met: undefined,
          // Fail-safe: whether the old 1-5 scores judged effectiveness is a
          // founder decision, not an assumption.
          effectivenessAssessed: false,
          evidence: null,
          assessedAt: legacy.updatedAt || null,
          legacyKey: legacy.key,
        },
      });
    }
  }
  const pick = latest(candidates.map((c) => ({ ...c.a, __source: c.source })));
  if (!pick) return { ...base, status: 'not-assessed', reason: 'no-assessment', assessment: null, source: null, expiresAt: null };

  const { __source: source, ...assessment } = pick;
  const assessedMs = toMs(assessment.assessedAt);
  if (!Number.isFinite(assessedMs)) {
    return { ...base, status: 'not-assessed', reason: 'assessment-undated', assessment, source, expiresAt: null };
  }

  let reason = null;
  if (requiredLevel !== null) {
    const level = Number(assessment.level);
    if (assessment.level == null || !Number.isFinite(level)) reason = 'level-not-recorded';
    else if (level < requiredLevel) reason = 'below-required-level';
  } else if (assessment.met !== true) {
    reason = 'assessed-not-met';
  }
  if (!reason && req.kind === 'skill' && assessment.effectivenessAssessed !== true) reason = 'effectiveness-not-assessed';
  if (!reason && req.evidenceRequired && !hasEvidence(assessment.evidence)) reason = 'evidence-missing';
  if (reason) return { ...base, status: 'gap', reason, assessment, source, expiresAt: null };
  // A legacy score is read (its level is shown) but never yields Met: its
  // updatedAt is client-written and moves on a target edit, so it cannot
  // carry the "assessed, with evidence, and current" claim Met makes (§6.2).
  // Only a recorded assessment can close it.
  if (source === 'legacy-role-score') {
    return { ...base, status: 'gap', reason: 'legacy-score-unverified', assessment, source, expiresAt: null };
  }

  // Monitoring is not an event: a met assessment lapses.
  const expiries = [];
  if (Number.isFinite(toMs(assessment.expiresAt))) expiries.push(toMs(assessment.expiresAt));
  const months = Number(req.reassessEveryMonths);
  if (Number.isFinite(months) && months > 0) expiries.push(addMonthsMs(assessedMs, months));
  const expiresMs = expiries.length ? Math.min(...expiries) : null;
  const expiresAt = expiresMs === null ? null : new Date(expiresMs).toISOString();
  if (expiresMs !== null && expiresMs <= at) {
    return { ...base, status: 'expired', reason: 'reassessment-due', assessment, source, expiresAt };
  }
  return { ...base, status: 'met', reason: null, assessment, source, expiresAt };
}

/**
 * Whether `employee` meets each competence requirement `role` stated as of
 * `asOf` (default now).
 *
 * @param {object} role         a roleDefinitions[] entry
 * @param {object} employee     an employees[] record
 * @param {object} [context]    { criteria, trainingModules, trainingPrograms, evidenceRecords } —
 *   `criteria` is required for any requirement naming a criterionId; without it
 *   (or with the criterion removed) that requirement is a gap, never met.
 * @param {string|number|Date} [asOf]
 * @returns {{ roleId, asOf, requirements: object[], counts: { met, gap, 'not-assessed', expired }, unmet: number }}
 */
function deriveCompetenceStatus(role, employee, context = {}, asOf) {
  const at = asOfMs(asOf);
  const ctx = context || {};
  const reqs = role ? competenceRequirementsAsOf(role, at) : [];
  const requirements = reqs.map((req) => statusOfRequirement(req, role, employee, ctx, at));
  const counts = { met: 0, gap: 0, 'not-assessed': 0, expired: 0 };
  for (const r of requirements) counts[r.status] += 1;
  return {
    roleId: role?.id ?? null,
    asOf: new Date(at).toISOString(),
    requirements,
    counts,
    unmet: requirements.length - counts.met,
  };
}

/**
 * The derivation context deriveCompetenceStatus reads, from tenant state. One
 * builder for the gap-task store and the SUP-05 metric, so both judge a
 * requirement against the same catalogue, modules and evidence.
 */
function competenceContextOf(state) {
  return {
    criteria: Array.isArray(state?.employeeEvaluationCriteria) ? state.employeeEvaluationCriteria : [],
    trainingModules: trainingModulesOf(state || {}),
    trainingPrograms: trainingProgramsOf(state || {}),
    evidenceRecords: Array.isArray(state?.evidenceRecords) ? state.evidenceRecords : [],
  };
}

/**
 * The role with its requirements replaced. `version` is deliberately left
 * alone: competence requirements are not part of what a holder acknowledged
 * (IDN-B-012), so changing them never asks anyone to re-acknowledge.
 */
function withCompetenceRequirements(role, requirements) {
  return { ...role, competenceRequirements: Array.isArray(requirements) ? requirements : [] };
}

// ── Server side: state-write's guard on roleDefinitions ─────────────────────

const REQ_TEXT_MAX = 1000;

/** A requirement in canonical form, or null when it is not valid. */
function normalizeRequirement(req) {
  if (!req || typeof req !== 'object') return null;
  const id = typeof req.id === 'string' ? req.id.trim() : '';
  const description = typeof req.description === 'string' ? req.description.trim() : '';
  if (!id || !COMPETENCE_KINDS.includes(req.kind) || !description || description.length > REQ_TEXT_MAX) return null;
  const out = { id, kind: req.kind, description, evidenceRequired: req.evidenceRequired === true };
  if (req.criterionId != null && req.criterionId !== '') {
    if (typeof req.criterionId !== 'string') return null;
    out.criterionId = req.criterionId;
    if (req.requiredLevel != null && req.requiredLevel !== '') {
      const lvl = Number(req.requiredLevel);
      if (!Number.isInteger(lvl) || lvl < 0) return null;
      out.requiredLevel = lvl;
    }
  } else if (req.requiredLevel != null && req.requiredLevel !== '') {
    return null; // a level means nothing without the scale it is on
  }
  for (const k of ['trainingModuleId', 'trainingProgramId']) {
    if (req[k] != null && req[k] !== '') {
      if (typeof req[k] !== 'string') return null;
      out[k] = req[k];
    }
  }
  if (req.trainingCompletionSuffices === true) {
    if (req.kind !== 'training' || (!out.trainingModuleId && !out.trainingProgramId)) return null;
    out.trainingCompletionSuffices = true;
  }
  if (req.reassessEveryMonths != null && req.reassessEveryMonths !== '') {
    const m = Number(req.reassessEveryMonths);
    if (!Number.isInteger(m) || m < 1 || m > 120) return null;
    out.reassessEveryMonths = m;
  }
  return out;
}

function normalizeRequirements(list) {
  if (list === undefined || list === null) return [];
  if (!Array.isArray(list)) return null;
  const out = [];
  const ids = new Set();
  for (const r of list) {
    const n = normalizeRequirement(r);
    if (!n || ids.has(n.id)) return null;
    ids.add(n.id);
    out.push(n);
  }
  return out;
}

function sameJson(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * state-write's roleDefinitions guard (restore, don't reject). For each posted
 * role:
 *   - competenceHistory is never taken from the payload — the stored copy
 *     stands, so no client can rewrite, back-date or drop a past requirement.
 *   - a valid change to competenceRequirements is stored in canonical form and
 *     appends one history entry stamped here ({ at, by, name, requirements }).
 *   - an invalid list is not stored: the stored requirements stand, and the
 *     role is reported back.
 *   - a role that carries no requirements and never did is passed through
 *     byte-for-byte, so a legacy role saves unchanged.
 *   - only an admin determines what a role requires (ISO 27001 7.2(a)). For
 *     anyone else a changed list is not stored: the stored requirements
 *     stand and the role is reported back — otherwise lowering a level or
 *     dropping a requirement would clear a gap nobody assessed. Nor may
 *     anyone else remove a role that carries requirements or history: it is
 *     put back. Fails closed: anything but an explicit `isAdmin: true` is a
 *     non-admin.
 *   - while the roleCompetence release flag is not ON (`allowAdd: false`), a
 *     requirement id the stored role lacks (a new role's included) is not
 *     stored and the role is reported back; the rest of the list — edits and
 *     removals of existing requirements — is stored as usual. Registering the
 *     flag never hides or strips what a tenant already has (RFLG-B-006).
 *     state-write always passes `allowAdd`; absent, adding is allowed.
 *
 * @param {object[]} posted   the payload's roleDefinitions
 * @param {object[]} stored   what is stored now
 * @param {{ at: string, by: string|null, isAdmin?: boolean, allowAdd?: boolean }} stamp
 * @returns {{ roleDefinitions: object[], reverted: {id, rule}[], stamped: string[],
 *   added: Record<string, string[]> }} `added`: per role id, the requirement ids
 *   this save stores that the stored role did not carry.
 */
function stampCompetenceHistory(posted, stored, stamp) {
  if (!Array.isArray(posted)) return { roleDefinitions: posted, reverted: [], stamped: [] };
  const storedById = new Map((Array.isArray(stored) ? stored : []).filter((r) => r && r.id).map((r) => [r.id, r]));
  const reverted = [];
  const stamped = [];
  const added = {};
  const out = posted.map((role) => {
    if (!role || typeof role !== 'object' || !role.id) return role;
    const prev = storedById.get(role.id);
    const prevReqs = requirementsOf(prev);
    const prevHistory = historyOf(prev);
    const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

    if (!has(role, 'competenceRequirements') && !has(role, 'competenceHistory') && prevReqs.length === 0 && prevHistory.length === 0) {
      return role; // legacy role, untouched
    }

    let next = { ...role };
    // History: always the stored copy.
    if (prevHistory.length) next.competenceHistory = prev.competenceHistory;
    else delete next.competenceHistory;
    if (has(role, 'competenceHistory') && !sameJson(role.competenceHistory, prevHistory.length ? prev.competenceHistory : undefined)) {
      reverted.push({ id: String(role.id), rule: 'competence-history-server-owned' });
    }

    const keepStored = () => {
      if (prev && has(prev, 'competenceRequirements')) next.competenceRequirements = prev.competenceRequirements;
      else delete next.competenceRequirements;
      return next;
    };
    // Absence is never deletion: a payload without the key (an older client)
    // keeps what is stored.
    if (!has(role, 'competenceRequirements')) return keepStored();
    let normalized = normalizeRequirements(role.competenceRequirements);
    if (normalized === null) {
      reverted.push({ id: String(role.id), rule: 'competence-requirements-invalid' });
      return keepStored();
    }
    const prevIds = new Set(prevReqs.map((r) => r && r.id));
    if (stamp?.allowAdd === false && normalized.some((r) => !prevIds.has(r.id))) {
      reverted.push({ id: String(role.id), rule: 'competence-requirements-not-released' });
      normalized = normalized.filter((r) => prevIds.has(r.id));
    }
    // Compared in canonical form, so a stored copy in another key order is not
    // mistaken for a change and stamped again.
    const changed = !sameJson(normalized, normalizeRequirements(prevReqs) ?? prevReqs);
    if (changed && stamp?.isAdmin !== true) {
      reverted.push({ id: String(role.id), rule: 'competence-requirements-admin-only' });
      return keepStored();
    }
    const addedIds = normalized.map((r) => r.id).filter((id) => !prevIds.has(id));
    if (addedIds.length) added[String(role.id)] = addedIds;
    next.competenceRequirements = normalized;
    const firstRecord = prevHistory.length === 0 && normalized.length > 0;
    if (changed || firstRecord) {
      next.competenceHistory = [
        ...(prevHistory.length ? prev.competenceHistory : []),
        { at: stamp.at, by: stamp.by ?? null, name: typeof role.name === 'string' ? role.name : '', requirements: normalized },
      ];
      stamped.push(String(role.id));
    }
    return next;
  });
  // A non-admin cannot remove a role that carries requirements or history:
  // that would clear every holder's gap unassessed and erase the record of
  // what the role required. Restore, don't reject.
  if (stamp?.isAdmin !== true) {
    const postedIds = new Set(posted.filter((r) => r && r.id).map((r) => String(r.id)));
    for (const prev of storedById.values()) {
      if (postedIds.has(String(prev.id))) continue;
      if (requirementsOf(prev).length === 0 && historyOf(prev).length === 0) continue;
      out.push(prev);
      reverted.push({ id: String(prev.id), rule: 'competence-role-admin-only' });
    }
  }
  return { roleDefinitions: out, reverted, stamped, added };
}

// ── Line manager ─────────────────────────────────────────────────────────────
// The one resolver for "who is this employee's line manager", as a USER id
// (the identity that signs in). Two fields are in use and both are read:
//   - `manager`: Entra sync writes the manager's EMPLOYEE id; the employee
//     form writes a tenant USER id.
//   - `managerId`: bulk edit writes it; employeeDerivations.js reads it.
// Every value is first resolved to exactly one employee RECORD — the one whose
// id is the value, or the one linked to the value as a user id — and the
// answer is that record's linkedUserId, which only an admin sets
// (employees-write handleSave). A value is never itself taken as a user id:
// employee ids and user ids share no trusted namespace, so a record whose id
// happens to equal someone's user id must not stand in for them. A value
// matching no record, or more than one, resolves to nobody. A sample person is
// never anyone's manager (GH #7870). When the two fields name different
// people — or one names a manager who cannot sign in — the answer is null:
// authority is never granted from an ambiguous record.
function lineManagerUserId(emp, employees) {
  if (!emp || typeof emp !== 'object') return null;
  const list = Array.isArray(employees) ? employees : [];
  const resolve = (v) => {
    if (typeof v !== 'string' || !v.trim()) return undefined; // field not set
    const id = v.trim();
    const matches = list.filter((e) => e && typeof e === 'object' && (e.id === id || e.linkedUserId === id));
    if (matches.length !== 1) return null;
    const m = matches[0];
    if (isSampleEmployee(m)) return null;
    return typeof m.linkedUserId === 'string' && m.linkedUserId ? m.linkedUserId : null;
  };
  const named = [resolve(emp.manager), resolve(emp.managerId)].filter((v) => v !== undefined);
  if (named.length === 0) return null;
  if (named.some((v) => v === null)) return null;
  return named.every((v) => v === named[0]) ? named[0] : null;
}

/** True when the caller (a user id) is the employee's line manager. */
function isLineManagerOf(userId, emp, employees) {
  if (typeof userId !== 'string' || !userId) return false;
  if (emp && emp.linkedUserId && String(emp.linkedUserId) === userId) return false; // never your own manager
  return lineManagerUserId(emp, employees) === userId;
}

// Who may record a competence assessment (RCQA-01, founder decision): a
// tenant admin, or — holding a role that may edit — the employee's line
// manager. Read-only roles (reader, prospect), auditor sessions and anyone
// else are refused. Returns { allowed, basis: 'admin'|'manager'|null, reason }.
const COMPETENCE_ADMIN_ROLES = Object.freeze(['admin', 'super-admin']);
const COMPETENCE_MANAGER_ROLES = Object.freeze(['manager']);
function competenceAssessorBasis(auth, emp, employees) {
  if (!auth || auth.type === 'auditor') return { allowed: false, basis: null, reason: 'role' };
  if (COMPETENCE_ADMIN_ROLES.includes(auth.role)) return { allowed: true, basis: 'admin', reason: null };
  if (!COMPETENCE_MANAGER_ROLES.includes(auth.role)) return { allowed: false, basis: null, reason: 'role' };
  const userId = auth.userId ? String(auth.userId) : '';
  if (isLineManagerOf(userId, emp, employees)) return { allowed: true, basis: 'manager', reason: null };
  return { allowed: false, basis: null, reason: 'not-line-manager' };
}

// The two fields that make someone a line manager — and so able to assess.
const LINE_MANAGER_FIELDS = Object.freeze(['manager', 'managerId']);
// Every employee field only an admin may set or change: the manager fields,
// and linkedUserId, through which the resolver above turns a manager record
// into a signed-in user (and the self-assessment check recognises "you").
const ASSESSOR_AUTHORITY_FIELDS = Object.freeze([...LINE_MANAGER_FIELDS, 'linkedUserId']);

module.exports = {
  lineManagerUserId,
  isLineManagerOf,
  competenceAssessorBasis,
  LINE_MANAGER_FIELDS,
  ASSESSOR_AUTHORITY_FIELDS,
  COMPETENCE_KINDS,
  COMPETENCE_STATUSES,
  competenceRequirementsAsOf,
  roleNamesOf,
  legacyRoleScore,
  deriveCompetenceStatus,
  competenceContextOf,
  withCompetenceRequirements,
  normalizeRequirement,
  normalizeRequirements,
  stampCompetenceHistory,
  addMonthsMs,
};
